import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { apiGetUserUiProps, apiPutUserUiProps } from '../utils/api';

const PreferencesContext = createContext();

const STORAGE_KEY = 'klaes_preferences'; // nur noch Fallback
const DEFAULT_PREFS = {
  accentColor: '#6366f1',
  fontSize: 100,
};

export const PreferencesProvider = ({ children }) => {
  const { token, user } = useAuth();
  const [preferences, setPreferences] = useState(DEFAULT_PREFS);
  const [loading, setLoading] = useState(true);

  // 1) Laden der UI-Props vom Server, sobald Token da ist
  useEffect(() => {
    if (!token) {
      // Nicht eingeloggt → Defaults, kein Laden
      setLoading(false);
      return;
    }

    const loadFromServer = async () => {
      setLoading(true);
      try {
        const data = await apiGetUserUiProps(token);
        const serverProps = data.ui_props || {};
        // Zusammführen mit Defaults (für neue Felder)
        const merged = {
          ...DEFAULT_PREFS,
          ...serverProps,
        };
        setPreferences(merged);
        applyPreferencesToCss(merged);
      } catch (err) {
        console.error('Fehler beim Laden der UI-Props', err);
        // Fallback: Lokale Werte oder Defaults
        const local = localStorage.getItem(STORAGE_KEY);
        if (local) {
          try {
            const localPrefs = JSON.parse(local);
            setPreferences(localPrefs);
            applyPreferencesToCss(localPrefs);
          } catch (e) {}
        }
      } finally {
        setLoading(false);
      }
    };

    loadFromServer();
  }, [token]);

  // 2) Bei Änderung an preferences → Backend updaten + localStorage + CSS
  const updatePreference = async (key, value) => {
    const newPrefs = { ...preferences, [key]: value };
    setPreferences(newPrefs);
    applyPreferencesToCss(newPrefs);

    // Lokaler Fallback (für Offline oder schnelle Wiederherstellung)
    localStorage.setItem(STORAGE_KEY, JSON.stringify(newPrefs));

    // Zum Server senden, wenn eingeloggt
    if (token) {
      try {
        // Hole aktuelle komplette UI-Props vom Server (falls zwischenzeitlich andere Felder geändert wurden)
        const currentData = await apiGetUserUiProps(token);
        const currentProps = currentData.ui_props || {};
        const updatedProps = { ...currentProps, ...newPrefs };
        await apiPutUserUiProps(token, updatedProps);
      } catch (err) {
        console.error('Fehler beim Speichern der UI-Props', err);
      }
    }
  };

  const resetToDefault = () => {
    updatePreference('accentColor', DEFAULT_PREFS.accentColor);
    updatePreference('fontSize', DEFAULT_PREFS.fontSize);
  };

  // Hilfsfunktion für CSS-Variablen (bleibt gleich)
  const applyPreferencesToCss = (prefs) => {
    document.documentElement.style.setProperty('--user-accent', prefs.accentColor);
    document.documentElement.style.setProperty(
      '--user-accent-light',
      lightenColor(prefs.accentColor, 20)
    );
    document.documentElement.style.setProperty(
      '--user-accent-dark',
      darkenColor(prefs.accentColor, 20)
    );
    const baseSize = (prefs.fontSize / 100) * 16;
    document.documentElement.style.setProperty('--base-font-size', `${baseSize}px`);
  };

  const lightenColor = (color, percent) => color; // Platzhalter
  const darkenColor = (color, percent) => color;

  return (
    <PreferencesContext.Provider value={{ preferences, updatePreference, resetToDefault, loading }}>
      {children}
    </PreferencesContext.Provider>
  );
};

export const usePreferences = () => useContext(PreferencesContext);